import { useEffect, useState } from "react";
import { PageHeader, Empty } from "@/components/UIBits";
import api from "@/lib/api";
import { Plus, Trash2, Loader2 } from "lucide-react";

export default function ProxyPool() {
  const [pool, setPool] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setPool(await api.listProxyPool());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    const numbers = input
      .split(/[\n,]+/)
      .map(n => n.trim())
      .filter(Boolean);
    if (!numbers.length) return;
    setLoading(true);
    try {
      await api.addProxyPool(numbers);
      setInput("");
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteProxyPool(id);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Cannot delete");
    }
  };

  const available = pool.filter(p => p.status === "available");
  const assigned = pool.filter(p => p.status === "assigned");

  return (
    <div data-testid="proxy-pool-page">
      <PageHeader
        title="Proxy Pool"
        subtitle="// Numbers available for self-serve signup"
        testid="proxy-pool-header"
      />

      {/* Add numbers */}
      <div className="mb-8">
        <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider block mb-2">
          Add proxy numbers (one per line or comma-separated)
        </label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          rows={3}
          placeholder={"+15551234567\n+15559876543"}
          className="w-full bg-[#0e0e10] border border-[#27272a] text-white font-mono text-sm px-4 py-3 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600 resize-none"
          data-testid="proxy-pool-input"
        />
        <button
          onClick={add}
          disabled={!input.trim() || loading}
          className="mt-2 flex items-center gap-1.5 bg-white text-black font-mono text-xs px-4 py-2 hover:bg-zinc-200 transition-colors disabled:opacity-50"
          data-testid="add-proxy-pool-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          Add to Pool
        </button>
      </div>

      {/* Available */}
      <h2 className="font-heading text-lg text-white mb-3">
        Available ({available.length})
      </h2>
      {available.length === 0 ? (
        <Empty testid="no-available-proxy">No proxy numbers available — add some above</Empty>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-10">
          {available.map(p => (
            <div
              key={p.id}
              className="border border-[#1f1f23] bg-[#0e0e10] p-3 flex items-center justify-between"
            >
              <code className="font-mono text-sm text-emerald-400">{p.number}</code>
              <button
                onClick={() => remove(p.id)}
                className="text-zinc-500 hover:text-red-400 p-1 transition-colors"
                data-testid={`delete-proxy-${p.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Assigned */}
      {assigned.length > 0 && (
        <>
          <h2 className="font-heading text-lg text-white mb-3">
            Assigned ({assigned.length})
          </h2>
          <div className="space-y-1">
            {assigned.map(p => (
              <div
                key={p.id}
                className="border border-[#1f1f23] bg-[#0a0a0c] p-3 flex items-center justify-between opacity-60"
              >
                <code className="font-mono text-sm text-zinc-500">{p.number}</code>
                <span className="font-mono text-[10px] text-zinc-600">
                  assigned to {p.assigned_to}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
